import { Pipe, PipeTransform } from "@angular/core";
import { changeTokenUnit, getCurrentDecimalSeperator, stripFunnyInput } from "@helpers/utils";

import {
	CurrencyPrecisionPipe,
	FiatPrecisionPipe,
	PrecisionInterface,
	TradablePrecisionPipe
} from "./currency.precision.pipe";
import { CurrencySymbolPipe, FiatSymbolPipe, TradableSymbolPipe } from "./currency.symbol.pipe";

import { Currency } from "@models/interfaces/currency";
import { Fiat } from "@models";
import { FiatService } from "@main-services/currency/fiat.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { LargeNumberPipe } from "./large-number.pipe";
import { LANGUAGES_WITH_PREFIXED_DENOMINATION } from "@helpers/constants";
import { TranslationService } from "@core-services/translation.service";

export abstract class CurrencyPrecisionSymbol<C extends Currency> implements PipeTransform {
	protected precision: CurrencyPrecisionPipe = null;
	protected currency: CurrencySymbolPipe<C> = null;

	public transform(val: string | number, args: PrecisionInterface): string {
		val = stripFunnyInput(val);
		if (!(this.precision && this.currency)) {
			return "";
		}

		let num = this.precision.transform(val, args);

		return this.getCurrencyOrder(num, args, val);
	}

	protected getCurrencyOrder(num: string | number, args: PrecisionInterface, rawValue?: string | number): string {
		const currency = this.currency.transform(args);

		const nonBreakingSpace = "\u00A0";
		const space = [...currency].length === 1 ? nonBreakingSpace : " ";

		if (this.currency.isCommodity(args) || args.valueFirst) {
			if (args.prefix && args.isDust && args.tradableAmount > 0) {
				return "<" + num + space + currency;
			}
			return num + space + currency;
		}
		if (args.symbol && !args.valueFirst && currency.length === 1) {
			if (args.prefix && args.isDust && args.tradableAmount > 0) {
				return "<" + currency + num;
			}
			return currency + num;
		}

		const currencyPrefix = args.removeCurrency ? "" : currency + nonBreakingSpace;
		return currencyPrefix + num;
	}
}

@Pipe({
	name: "tradable_precision_symbol",
	standalone: false
})
export class TradablePrecisionSymbolPipe extends CurrencyPrecisionSymbol<Tradable> implements PipeTransform {
	public constructor(
		private tradableListService: TradableListService,
		private translationService: TranslationService
	) {
		super();
		this.precision = new TradablePrecisionPipe(tradableListService);
		this.currency = new TradableSymbolPipe(tradableListService, translationService);
	}

	protected getCurrencyOrder(num: string | number, args: PrecisionInterface, rawValue: string | number): string {
		let currency = this.currency.transform(args);

		let tradable: Tradable;
		if (args?.assetPid) {
			tradable = this.tradableListService.getItemByPid(args.assetPid);
		} else {
			tradable = this.tradableListService.getItemById(args.id);
		}

		if (tradable?.isToken()) {
			// Resolve problems for amount in some languages when value is a string value
			currency = changeTokenUnit(rawValue);
			const translateTokenCurrency = this.translationService.translate(currency);

			if (args.onlyUnit) {
				return translateTokenCurrency;
			}
			return num + " " + translateTokenCurrency;
		}

		if (args.onlyUnit) {
			return currency;
		}
		return num + " " + currency;
	}
}

@Pipe({
	name: "fiat_precision_symbol",
	standalone: false
})
export class FiatPrecisionSymbolPipe extends CurrencyPrecisionSymbol<Fiat> implements PipeTransform {
	private largeNumber: LargeNumberPipe = null;

	public constructor(
		private fiatService: FiatService,
		private translationService: TranslationService
	) {
		super();
		this.precision = new FiatPrecisionPipe();
		this.currency = new FiatSymbolPipe(fiatService, translationService);
		this.largeNumber = new LargeNumberPipe(translationService);
	}

	public transform(val: string | number, args: PrecisionInterface): string {
		if (!(this.precision && this.currency)) {
			return "";
		}

		if (!args.valueFirst && LANGUAGES_WITH_PREFIXED_DENOMINATION.includes(this.translationService.getLanguage())) {
			args.valueFirst = true;
		}

		let num = val;
		if (args.largeNumber && isNaN(+this.largeNumber.transform(num))) {
			// Shorten only if enabled and the number is big enough
			num = this.largeNumber.transform(num, args.round);
		} else {
			num = this.precision.transform(val, args);
		}

		if (args.noTrailingZeros) {
			// remove trailing zeros from string
			const regexPattern = getCurrentDecimalSeperator() === "." ? /\.?0+$/ : /,?0+$/;
			num = num.replace(regexPattern, "");
		}

		return this.getCurrencyOrder(num, args);
	}
}
